import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
} from "@angular/forms";

import { UserService } from "./../../shared/services/user.service";
import { AuthService } from "./../auth.service";
import { AppSettingsService } from "./../../shared/services/app-settings.service";
import { ViewStateService } from "./../../shared/services/view-state.service";
import { NotificationsService } from "./../../shared/services/notifications.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-change-password",
  templateUrl: "./change-password.component.html",
})
export class ChangePasswordComponent implements OnInit, OnDestroy {
  changePasswordForm: UntypedFormGroup;
  formError: string;
  changePasswordSuccess: boolean = false;
  private destroy$ = new Subject();

  private formValues = {
    oldPassword: "",
    newPassword: "",
    confirmation: "",
  };

  formMessages = {
    oldPassword: "",
    newPassword: "",
    confirmation: "",
  };

  private validationMessages = {
    oldPassword: {
      required: "Old Password is required.",
      passwordComplexity:
        "Password must meet complexity requirements (one number, one uppercase letter, one symbol, 8 characters).",
    },
    newPassword: {
      required: "New Password is required.",
      passwordComplexity:
        "Password must meet complexity requirements (one number, one uppercase letter, one symbol, 8 characters).",
    },
    confirmation: {
      required: "Password Confirmation is required.",
      validateEqual: "Passwords do not match.",
    },
    form: {
      request: "Change password request failed.",
    },
  };

  constructor(
    private appSettings: AppSettingsService,
    private authService: AuthService,
    private userService: UserService,
    private viewState: ViewStateService,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private notifyService: NotificationsService
  ) {}

  ngOnInit() {
    this.buildForm();
  }

  onSubmit(updatedFormValue: any) {
    this.viewState.startLoading();
    this.formValues = updatedFormValue;
    this.validateForm();
    this.changePassword();
  }

  private changePassword() {
    const currentUser = this.userService.CurrentUser;

    this.formError = "";
    if (this.changePasswordForm.valid) {
      this.authService
        .changePassword(
          currentUser.personId,
          currentUser.email,
          this.formValues.oldPassword,
          this.formValues.newPassword,
          this.formValues.confirmation
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (success: boolean) => {
            this.notifyService.success("Success", "Password was updated.");
            this.changePasswordSuccess = true;
            this.viewState.stopLoading();
          },
          (error) => {
            this.notifyService.error("Error", "The password was not updated.");
            this.formError = this.validationMessages.form.request;
            this.viewState.stopLoading();
          }
        );
    }
  }

  private buildForm() {
    this.changePasswordForm = this.formBuilder.group({
      oldPassword: [this.formValues.newPassword, Validators.required],
      newPassword: [
        this.formValues.oldPassword,
        [Validators.required, this.passwordComplexity],
      ],
      confirmation: [this.formValues.confirmation, [Validators.required]],
    });
  }

  private validateForm() {
    if (!this.changePasswordForm) {
      return;
    }

    for (const control in this.changePasswordForm.controls)
      this.changePasswordForm.controls[control].updateValueAndValidity();

    var hasErrors = false;

    for (const field in this.formMessages) {
      this.formMessages[field] = "";
      const control = this.changePasswordForm.get(field);
      if (control && !control.valid) {
        hasErrors = true;
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formMessages[field] += messages[key] + " ";
        }
      }
    }
    if (hasErrors) this.viewState.stopLoading();
  }

  private passwordComplexity(c: UntypedFormControl) {
    if (!c.value) return null;

    var valid = true;
    if (!c.value.match(/\d/)) valid = false;
    if (!c.value.match(/[A-Z]/)) valid = false;
    if (!c.value.match(/[a-z]/)) valid = false;
    if (!c.value.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/)) valid = false;
    if (!c.value.match(/^.{8,255}$/)) valid = false;

    if (valid) return null;

    return { passwordComplexity: { valid: "invalid" } };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
